import * as otel from "@opentelemetry/api";
import { mustNeverExist } from "@repo/utils";

type StartActiveSpanArgs<T> =
    | [name: string, fn: (span: otel.Span) => T]
    | [
          name: string,
          options: otel.SpanOptions & {
              resultAttributes?: (x: Awaited<T>) => otel.Attributes;
              langsmith?: boolean;
          },
          fn: (span: otel.Span) => T,
      ]
    | [
          name: string,
          options: otel.SpanOptions & {
              resultAttributes?: (x: Awaited<T>) => otel.Attributes;
              langsmith?: boolean;
          },
          context: otel.Context,
          fn: (span: otel.Span) => T,
      ];

export const traces = {
    span<T>(...args: StartActiveSpanArgs<T>) {
        function wrap(fn: (span: otel.Span) => T) {
            return (span: otel.Span): T => {
                let endTime: number | undefined = undefined;
                let sync = false;
                try {
                    const result = fn(span);
                    if (result instanceof Promise) {
                        return result
                            .then((x: Awaited<T>) => {
                                endTime = performance.now();
                                if (args.length === 3 || args.length === 4) {
                                    const mapResult = args[1].resultAttributes;
                                    if (mapResult) {
                                        span.setAttributes(mapResult(x));
                                    }
                                }
                                return x;
                            })
                            .catch((e: unknown) => {
                                endTime = performance.now();
                                if (span.isRecording()) {
                                    const exception = e instanceof Error ? e : new Error(JSON.stringify(e));
                                    span.recordException(exception);
                                    span.setStatus({
                                        code: otel.SpanStatusCode.ERROR,
                                        message: exception.message,
                                    });
                                }
                                throw e;
                            })
                            .finally(() => {
                                if (span.isRecording()) {
                                    span.end(endTime);
                                }
                            }) as T;
                    }
                    sync = true;
                    endTime = performance.now();
                    if (args.length === 3 || args.length === 4) {
                        const mapResult = args[1].resultAttributes;
                        if (mapResult) {
                            span.setAttributes(mapResult(result as Awaited<T>));
                        }
                    }
                    return result;
                } catch (e) {
                    endTime = performance.now();
                    if (span.isRecording()) {
                        const exception = e instanceof Error ? e : new Error(JSON.stringify(e));
                        span.recordException(exception);
                        span.setStatus({
                            code: otel.SpanStatusCode.ERROR,
                            message: exception.message,
                        });
                    }
                    throw e;
                } finally {
                    if (sync && span.isRecording()) {
                        span.end(endTime);
                    }
                }
            };
        }

        const tracer = otel.trace.getTracer("default");
        switch (args.length) {
            case 2: {
                return tracer.startActiveSpan(args[0], wrap(args[1]));
            }
            case 3: {
                const { resultAttributes, ...options } = args[1];
                return tracer.startActiveSpan(args[0], options, wrap(args[2]));
            }
            case 4: {
                const { resultAttributes, ...options } = args[1];
                return tracer.startActiveSpan(args[0], options, args[2], wrap(args[3]));
            }
            default:
                throw mustNeverExist(args);
        }
    },
    getActiveSpan() {
        return (
            otel.trace.getActiveSpan() ?? otel.trace.wrapSpanContext({ spanId: "noop", traceFlags: 0, traceId: "noop" })
        );
    },
};
